import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Space, Typography, message } from 'antd';
import { SendOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import './index.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

// 接收：新增编辑回调 onUpdatePost + 编辑的文章数据 editPost + 路由参数（可选）
const WritePost = ({ onAddPost, onUpdatePost, editPost = null }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 判断是否为编辑模式（有 editPost 则为编辑）
  const isEditMode = !!editPost;

  // 标签选项（保留原有）
  const tagOptions = [
    { label: '前端', value: '前端' },
    { label: '后端', value: '后端' },
    { label: '全栈', value: '全栈' },
    { label: '生活', value: '生活' },
    { label: '技术杂谈', value: '技术杂谈' },
  ];

  // ========== 新增：编辑模式 - 填充表单数据 ==========
  useEffect(() => {
    if (isEditMode && editPost) {
      // 适配原有 tags 结构（数组转单个值，匹配 Select 组件）
      const tagValue = editPost.tags?.length > 0 ? editPost.tags[0] : '';
      // 填充表单（content 保留原始格式，不做任何处理）
      form.setFieldsValue({
        title: editPost.title || '',
        summary: editPost.summary || '',
        tag: tagValue,
        content: editPost.content || '', // 保留原始换行/空格
      });
    } else {
      // 新建模式：重置表单
      form.resetFields();
    }
  }, [isEditMode, editPost, form]);

  // ========== 优化：提交处理（区分新建/编辑） ==========
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      console.log(isEditMode ? '✏️ 编辑文章数据：' : '📝 提交的文章数据：', values);

      // 组装文章数据（匹配原有结构，content 保留原始值，不 trim）
      const postData = {
        // 编辑模式保留原有 ID，保证更新时能定位到文章
        id: isEditMode ? editPost.id : Date.now(), 
        title: values.title,
        summary: values.summary,
        tags: [values.tag], // 适配原有 tags 数组逻辑
        content: values.content, // 核心：保留原始输入（含换行/空格），不做任何过滤
        // 编辑模式新增更新时间，新建模式新增创建时间
        createTime: isEditMode ? editPost.createTime : new Date().toISOString(),
        updateTime: isEditMode ? new Date().toISOString() : '',
      };

      // 模拟接口请求（保留原有延迟逻辑）
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (isEditMode) {
        // 编辑模式：调用更新回调
        onUpdatePost?.(postData);
        message.success('文章编辑成功！', 2);
      } else {
        // 新建模式：调用原有发布回调
        onAddPost(postData);
        message.success('文章发布成功！', 2);
        form.resetFields(); // 新建后重置表单
      }
    } catch (errorInfo) {
      console.error('❌ 表单校验失败：', errorInfo);
      message.error('请完善必填项后重试！');
    } finally {
      setLoading(false);
    }
  };

  // 重置表单（保留原有，编辑模式下重置为原文章数据）
  const handleReset = () => {
    if (isEditMode) {
      // 编辑模式：重置为编辑前的原始数据（保留换行/空格）
      const tagValue = editPost.tags?.length > 0 ? editPost.tags[0] : '';
      form.setFieldsValue({
        title: editPost.title || '',
        summary: editPost.summary || '',
        tag: tagValue,
        content: editPost.content || '', // 保留原始格式
      });
      message.info('已重置为编辑前的内容');
    } else {
      // 新建模式：清空表单
      form.resetFields();
      message.info('表单已重置');
    }
  };

  return (
    <div className="write-post-page">
      <div className="write-post-wrapper">
        <div className="write-post-header">
          {/* 标题区分：新建/编辑 */}
          <Title level={2} className="write-post-title">
            {isEditMode ? (
              <Space>
                <EditOutlined /> 编辑文章
              </Space>
            ) : (
              '撰写新文章'
            )}
          </Title>
          <Text type="secondary">
            {isEditMode 
              ? '修改后点击「保存修改」即可更新文章（支持换行/空格）' 
              : '带 * 为必填项，内容支持换行/空格，兼容 Markdown 格式'
            }
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          className="write-post-form"
          initialValues={{ tag: '' }}
          size="middle"
          validateTrigger={['onSubmit', 'onBlur']}
        >
          {/* 文章标题（保留原有校验） */}
          <Form.Item
            name="title"
            label="文章标题 *"
            rules={[
              { required: true, message: '请输入文章标题' },
              { max: 50, message: '标题长度不能超过 50 个字' },
              { pattern: /^\S.*$/, message: '标题不能以空格开头/全为空格' },
            ]}
            tooltip={{ title: '建议标题简洁明了，突出核心内容' }}
          >
            <Input
              placeholder={isEditMode ? '修改文章标题...' : '如：AntD 6.1.1 表单开发实战'}
              autoFocus
              className="title-input"
            />
          </Form.Item>

          {/* 文章摘要（保留原有校验） */}
          <Form.Item
            name="summary"
            label="文章摘要 *"
            rules={[
              { required: true, message: '请输入文章摘要' },
              { max: 200, message: '摘要长度不能超过 200 个字' },
            ]}
          >
            <TextArea
              placeholder={isEditMode ? '修改文章摘要...' : '简要描述文章核心内容，帮助读者快速了解'}
              maxLength={200}
              showCount
              rows={3}
            />
          </Form.Item>

          {/* 标签选择（保留原有逻辑） */}
          <Form.Item
            name="tag"
            label="标签 *"
            rules={[{ required: true, message: '请选择文章标签' }]}
          >
            <Select
              placeholder={isEditMode ? '修改文章标签' : '搜索或选择标签'}
              options={tagOptions}
              allowClear
              showSearch
              filterOption={(inputValue, option) =>
                (option?.label ?? '').toLowerCase().includes(inputValue?.toLowerCase() ?? '')
              }
              dropdownMatchSelectWidth={false}
              style={{ width: '100%' }}
            />
          </Form.Item>

          {/* 文章内容（核心修改：保留换行/空格） */}
          <Form.Item
            name="content"
            label="文章内容（支持换行/空格 + Markdown）*"
            rules={[{ required: true, message: '请输入文章正文' }]}
          >
            <TextArea
              placeholder={
                isEditMode 
                  ? '修改你的文章内容（支持换行/空格、Markdown）...' 
                  : '## 二级标题\n**加粗文本**\n  （这里是2个空格）\n- 列表项1\n- 列表项2'
              }
              autoSize={{ minRows: 15, maxRows: 30 }}
              autoScrollIntoView={false}
              style={{ 
                whiteSpace: 'pre-wrap', // 核心：保留输入的换行/空格
                fontFamily: 'inherit', 
                lineHeight: 1.8 // 优化可读性
              }}
            />
          </Form.Item>

          {/* 操作按钮：区分新建/编辑 */}
          <Form.Item>
            <Space size="large">
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={handleReset}
                disabled={loading}
              >
                重置
              </Button>
              <Button
                type="primary"
                icon={isEditMode ? <EditOutlined /> : <SendOutlined />}
                onClick={handleSubmit}
                size="large"
                loading={loading}
              >
                {isEditMode ? '保存修改' : '发布文章'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default WritePost;